import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, DollarSign, Globe, FileWarning, Gavel, TrendingDown, ArrowRight } from 'lucide-react';
import { slideUp, staggerContainer } from '../utils/animations';
import { trackCTAClick } from '../utils/analytics';
export function ProblemSection() {
  return <section className="py-20 md:py-28 lg:py-32 bg-white relative overflow-hidden" data-id="element-251">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-252">
        <motion.div animate={{
        scale: [1, 1.2, 1],
        opacity: [0.1, 0.2, 0.1]
      }} transition={{
        duration: 8,
        repeat: Infinity
      }} className="absolute -top-1/4 -right-1/4 w-1/2 h-1/2 bg-red-500 rounded-full blur-[120px]" data-id="element-253" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-254">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20" data-id="element-255">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={slideUp} className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4 md:mb-6 tracking-tight" data-id="element-256">
            The high cost of <br data-id="element-257" />
            <motion.span className="text-red-600" animate={{
            opacity: [1, 0.7, 1]
          }} transition={{
            duration: 2,
            repeat: Infinity
          }} data-id="element-258">
              trademark infringement
            </motion.span>
          </motion.h2>
          <motion.p initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={slideUp} className="text-base md:text-lg text-slate-600 leading-relaxed" data-id="element-259">
            Counterfeits and unauthorized use dilute your brand value and
            confuse customers. Without automated monitoring, you're fighting a
            losing battle.
          </motion.p>
        </div>

        {/* Visual Threat Timeline */}
        <div className="relative max-w-4xl mx-auto" data-id="element-260">
          {/* Central Line with animated gradient */}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 -translate-x-1/2 hidden md:block overflow-hidden" data-id="element-261">
            <motion.div animate={{
            y: ['0%', '100%']
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'linear'
          }} className="w-full h-32 bg-gradient-to-b from-transparent via-red-400 to-transparent" data-id="element-262" />
            <div className="absolute inset-0 bg-gradient-to-b from-slate-200 via-red-200 to-slate-200" data-id="element-263" />
          </div>
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-200 md:hidden" data-id="element-264"></div>

          <div className="space-y-12 md:space-y-24 relative z-10" data-id="element-265">
            {/* Stage 1 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} className="flex flex-col md:flex-row items-center gap-8" data-id="element-266">
              <div className="flex-1 md:text-right order-2 md:order-1 pl-16 md:pl-0" data-id="element-267">
                <motion.h3 whileHover={{
                x: -5
              }} className="text-xl font-bold text-slate-900 mb-2" data-id="element-268">
                  Infringement Begins
                </motion.h3>
                <p className="text-slate-600" data-id="element-269">
                  Bad actors register lookalike domains and list counterfeit
                  products using your registered trademarks.
                </p>
              </div>
              <div className="relative flex-shrink-0 order-1 md:order-2" data-id="element-270">
                <motion.div whileHover={{
                scale: 1.1,
                rotate: 5
              }} className="w-12 h-12 rounded-full bg-white border-4 border-slate-100 flex items-center justify-center shadow-sm z-10 relative cursor-default" data-id="element-271">
                  <FileWarning className="w-5 h-5 text-slate-400" data-id="element-272" />
                </motion.div>
              </div>
              <div className="flex-1 order-3 hidden md:block" data-id="element-273"></div>
            </motion.div>

            {/* Stage 2 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="flex flex-col md:flex-row items-center gap-8" data-id="element-274">
              <div className="flex-1 order-3 md:order-1 hidden md:block" data-id="element-275"></div>
              <div className="relative flex-shrink-0 order-1 md:order-2" data-id="element-276">
                <motion.div whileHover={{
                scale: 1.1,
                rotate: -5
              }} className="w-12 h-12 rounded-full bg-amber-50 border-4 border-white ring-4 ring-amber-50 flex items-center justify-center shadow-sm z-10 relative cursor-default" data-id="element-277">
                  <Globe className="w-5 h-5 text-amber-500" data-id="element-278" />
                </motion.div>
              </div>
              <div className="flex-1 order-2 md:order-3 pl-16 md:pl-0" data-id="element-279">
                <motion.h3 whileHover={{
                x: 5
              }} className="text-xl font-bold text-slate-900 mb-2" data-id="element-280">
                  Market Confusion
                </motion.h3>
                <p className="text-slate-600" data-id="element-281">
                  Customers buy fake goods thinking they are yours. Negative
                  reviews pile up for products you didn't sell.
                </p>
              </div>
            </motion.div>

            {/* Stage 3 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6,
            delay: 0.4
          }} className="flex flex-col md:flex-row items-center gap-8" data-id="element-282">
              <div className="flex-1 md:text-right order-2 md:order-1 pl-16 md:pl-0" data-id="element-283">
                <motion.h3 whileHover={{
                x: -5
              }} className="text-xl font-bold text-slate-900 mb-2" data-id="element-284">
                  Revenue & Brand Loss
                </motion.h3>
                <p className="text-slate-600" data-id="element-285">
                  Direct revenue is stolen, and your trademark's distinctiveness
                  is diluted, weakening your legal standing.
                </p>
              </div>
              <div className="relative flex-shrink-0 order-1 md:order-2" data-id="element-286">
                <motion.div whileHover={{
                scale: 1.1,
                rotate: 5
              }} className="w-12 h-12 rounded-full bg-red-50 border-4 border-white ring-4 ring-red-50 flex items-center justify-center shadow-sm z-10 relative cursor-default" data-id="element-287">
                  <TrendingDown className="w-5 h-5 text-red-500" data-id="element-288" />
                </motion.div>
              </div>
              <div className="flex-1 order-3 hidden md:block" data-id="element-289">
                <motion.div whileHover={{
                scale: 1.05
              }} className="bg-red-50 p-4 rounded-lg border border-red-100 inline-block cursor-default" data-id="element-290">
                  <div className="text-red-800 font-bold text-2xl" data-id="element-291">
                    Dilution
                  </div>
                  <div className="text-red-600 text-xs uppercase font-bold tracking-wider" data-id="element-292">
                    Legal Risk
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>

          {/* CTA Card at bottom of timeline */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} whileHover={{
          scale: 1.02
        }} className="mt-20 bg-slate-900 rounded-2xl p-8 md:p-12 text-center relative overflow-hidden cursor-default" data-id="element-293">
            <div className="absolute top-0 left-0 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10" data-id="element-294"></div>
            <motion.div animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3]
          }} transition={{
            duration: 4,
            repeat: Infinity
          }} className="absolute -top-24 -right-24 w-64 h-64 bg-indigo-500 rounded-full blur-[100px]" data-id="element-295" />

            <div className="relative z-10" data-id="element-296">
              <motion.div animate={{
              rotate: [0, 10, -10, 0]
            }} transition={{
              duration: 3,
              repeat: Infinity
            }} className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-297">
                <Gavel className="w-8 h-8 text-amber-400" data-id="element-298" />
              </motion.div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4" data-id="element-299">
                Enforce your IP rights automatically
              </h3>
              <p className="text-slate-300 mb-8 max-w-xl mx-auto" data-id="element-300">
                Watchdog detects infringement instantly and automates the
                evidence collection needed for legal takedowns.
              </p>
              <a href="https://app.watchdog.buzz/" target="_blank" rel="noopener noreferrer" onClick={() => trackCTAClick('problem_section')} data-id="element-301">
                <motion.button whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} className="bg-white text-slate-900 hover:bg-slate-100 px-10 py-4 rounded-full font-bold transition-all shadow-lg text-lg inline-flex items-center gap-2" data-id="element-302">
                  Start Free Trial
                  <ArrowRight className="w-5 h-5" data-id="element-303" />
                </motion.button>
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}